import { cx } from "cva";
import NextImage from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { TagToClassNameMap } from "~/lib/shared/html-utils";
import { Body, Link } from "~/ui-library";

export type AuthorBioProps = {
  authorName: string;
  /** Human-readable id for the author. This is the id used for analytics */
  authorNicename: string;
  authorTitle: string;
  authorBioHTML: string | null;
  authorEmail: string;
  className?: string;
  href: string;
  headshotImageUrl: string | null;
};

const tagToClassNameMap: TagToClassNameMap = new Map([
  ["a", "text-gray-900 font-semibold"],
  ["p", "mb-m last:mb-0 body-regular"],
]);

export function AuthorBio({
  authorBioHTML,
  authorName,
  authorNicename,
  authorTitle,
  className,
  headshotImageUrl,
  href,
}: AuthorBioProps) {
  const { analyticsEnhancedTrack } = useAnalytics();

  const onLinkClick = () => {
    analyticsEnhancedTrack("Content: Author Click", {
      selectedAuthor: authorNicename,
    });
  };

  const image = headshotImageUrl ? (
    <NextImage
      alt={`A headshot photograph of ${authorName}`}
      className="size-[3rem]"
      height={48}
      src={headshotImageUrl}
      width={48}
    />
  ) : null;

  const imageLink =
    href && image ? (
      <Link className="min-w-fit" href={href} onClick={onLinkClick}>
        {image}
      </Link>
    ) : null;

  return (
    <div className={cx("flex flex-col gap-s", className)}>
      <div className="flex gap-s">
        {imageLink}

        {/* Author name and title, follow button */}
        <div className="flex w-full justify-between">
          <div className="flex flex-col justify-between gap-xxs">
            {href ? (
              <Link className="font-semibold" href={href} onClick={onLinkClick}>
                {authorName}
              </Link>
            ) : (
              <Body bold>{authorName}</Body>
            )}
            <Body size="small">{authorTitle}</Body>
          </div>
        </div>
      </div>

      {/* Author Bio */}
      {authorBioHTML ? (
        <div className="text-gray-700">
          <BuildHTML
            html={authorBioHTML}
            tagToClassNameMap={tagToClassNameMap}
          />
        </div>
      ) : null}
    </div>
  );
}
